import { getClientEnv } from "@shared/env";
import type { RefreshResponse } from "@shared/types";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { runRefresh } from "./refresh-lock";

const env = getClientEnv();

export const apiClient = axios.create({
	baseURL: `${env.VITE_API_URL}/api/v1`,
	headers: { "Content-Type": "application/json" },
	// Refresh cookie'ni yuborish uchun majburiy.
	withCredentials: true,
});

/**
 * Refresh uchun alohida instance — interceptor'siz, aks holda refresh so'rovining
 * o'zi 401 olganda cheksiz rekursiyaga tushadi.
 */
const refreshClient = axios.create({
	baseURL: `${env.VITE_API_URL}/api/v1`,
	headers: { "Content-Type": "application/json" },
	withCredentials: true,
});

/** Cookie'ga tayanib yangi access token oladi. Muvaffaqiyatsizlikda throw qiladi. */
export async function refreshAccessToken(): Promise<string> {
	const response = await refreshClient.post<RefreshResponse>("/auth/refresh", {});
	const { user, accessToken } = response.data.data;

	useAuthStore.getState().setAuth(user, accessToken);

	return accessToken;
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const { accessToken } = useAuthStore.getState();
	if (accessToken) {
		config.headers.Authorization = `Bearer ${accessToken}`;
	}
	return config;
});

apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as
			| (InternalAxiosRequestConfig & { _retry?: boolean })
			| undefined;

		if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			// Single-flight: parallel 401'lar bitta refresh'ni kutadi. Bu shart —
			// backend tokenni rotatsiya qiladi va parallel refresh reuse detection'ni
			// ishga tushirib barcha sessiyalarni bekor qiladi.
			const accessToken = await runRefresh(refreshAccessToken);

			originalRequest.headers.Authorization = `Bearer ${accessToken}`;
			return apiClient(originalRequest);
		} catch {
			useAuthStore.getState().logout();
			if (window.location.pathname !== "/login") {
				window.location.href = "/login";
			}
			return Promise.reject(error);
		}
	}
);

export default apiClient;
